/**
 * Organizations + memberships + constitution — Zod schemas (Milestone 1.5a).
 *
 * Mirrors:
 *   - chaos.organizations           (packages/db/src/schema.ts → organizations)
 *   - chaos.org_memberships         (org_memberships)
 *   - chaos.constitutions           (constitutions)
 *   - chaos.constitution_history    (constitution_history)
 *
 * Wire convention follows the rest of @chaos/types: snake_case columns
 * are exposed as camelCase on the API, timestamps as ISO strings, uuids
 * as plain strings. Soft-delete (archived_at) is nullable ISO string.
 */
import { z } from 'zod';

// ── Org membership roles (mirrors org_memberships_org_role_check) ────
export const orgRoles = ['founder', 'admin', 'member', 'viewer'] as const;
export const orgRoleSchema = z.enum(orgRoles);
export type OrgRole = z.infer<typeof orgRoleSchema>;

// ── Org kind (Org Architecture Phase F; mirrors organizations_kind_check) ──
// 'company' (default) or 'personal' (the founder's Private org). The Phase-F
// navigation rail gives a 'personal' org a DISTINCT identity (dashed ring /
// person glyph). Mirrors chaos.organizations.kind (0061).
export const orgKinds = ['company', 'personal'] as const;
export const orgKindSchema = z.enum(orgKinds);
export type OrgKind = z.infer<typeof orgKindSchema>;

// ── Account (Org Architecture Phase A) ───────────────────────────────
// The greenfield tenancy layer ABOVE organizations. One account == one
// owning user. Mirrors chaos.accounts (schema.ts → accounts).
export const accountSchema = z.object({
  id: z.string().uuid(),
  ownerUserId: z.string().uuid(),
  name: z.string().nullable(),
  plan: z.string().nullable(),
  createdAt: z.string().datetime().nullable(),
});
export type Account = z.infer<typeof accountSchema>;

// ── Organization ─────────────────────────────────────────────────────
export const organizationSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1).max(120),
  displayName: z.string().min(1).max(200),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  founderUserId: z.string().uuid().nullable(),
  // W3-1 / Org Architecture Stage 4 (0314) — every Workspace has an owner.
  accountId: z.string().uuid(),
  // Org Architecture Phase F — org kind (0061). Defaults 'company';
  // .catch keeps older gateways (pre-0061 payloads without the field)
  // parsing cleanly rather than throwing on the orgs list.
  kind: orgKindSchema.catch('company'),
  // Settings V2 (0064) — drag-to-reorder position. NULL when never
  // reordered (the gateway then falls back to created_at order). Kept
  // nullable (not optional) so the inferred type stays `number | null`.
  position: z.number().nullable().catch(null),
  // Settings V2 (0064) — per-org logo URL (gateway-served HMAC-token URL
  // or e2 presigned url). NULL = fall back to the auto identity. Not a
  // strict .url() so the gateway-relative "/api/orgs/:id/logo" form passes.
  logoUrl: z.string().nullable().catch(null),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // How many people are in this Workspace, the caller included (2026-08-01).
  // GET /api/orgs computes it; the other org routes return a single row and do
  // not, so `.catch(null)` keeps THEIR payloads parsing rather than throwing —
  // null means "this endpoint did not say", never "nobody is in it".
  //
  // It exists so the Workspace list can show which Workspaces are shared and
  // which are private without N per-row member fetches.
  memberCount: z.number().int().nullable().catch(null),
});
export type Organization = z.infer<typeof organizationSchema>;

export const createOrgSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase alphanumeric + dashes only'),
  displayName: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type CreateOrgInput = z.infer<typeof createOrgSchema>;

export const updateOrgSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
  // Settings V2 (0064) — set/clear the per-org logo URL. The dedicated
  // upload route normally writes this; exposed on PATCH too so a logo can
  // be cleared (logoUrl: null) without a multipart request. undefined =
  // leave alone; null = clear (fall back to auto identity).
  logoUrl: z.string().max(2000).nullable().optional(),
});
export type UpdateOrgInput = z.infer<typeof updateOrgSchema>;

// Settings V2 (0064) — drag-to-reorder companies (organizations) within an
// account. Mirrors reorderDivisionSchema / reorderOrgScopedColumnsSchema:
// post 1-based consecutive positions for the dragged set. Owner-gated
// server-side (account owner only).
export const reorderOrgSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid(),
        position: z.number(),
      }),
    )
    .min(1),
});
export type ReorderOrgInput = z.infer<typeof reorderOrgSchema>;

// ── Memberships ──────────────────────────────────────────────────────
export const orgMembershipSchema = z.object({
  orgId: z.string().uuid(),
  userId: z.string().uuid(),
  orgRole: orgRoleSchema,
  createdAt: z.string().datetime(),
});
export type OrgMembership = z.infer<typeof orgMembershipSchema>;

export const createMembershipSchema = z.object({
  userId: z.string().uuid(),
  orgRole: orgRoleSchema,
});
export type CreateMembershipInput = z.infer<typeof createMembershipSchema>;

export const updateMembershipSchema = z.object({
  orgRole: orgRoleSchema,
});
export type UpdateMembershipInput = z.infer<typeof updateMembershipSchema>;

// ── Invitations (SPEC-01 Stage 3; mirrors chaos.org_invitations, 0162) ──
//
// `founder` is absent from this enum for the same reason it is absent from the
// table's CHECK: the last-founder guard is what keeps a Workspace from being
// orphaned, and an invite that could mint founders would let a link in an email
// route around it. Founder handover stays a deliberate two-step act by a human.
export const invitableOrgRoles = ['admin', 'member', 'viewer'] as const;
export const invitableOrgRoleSchema = z.enum(invitableOrgRoles);
export type InvitableOrgRole = z.infer<typeof invitableOrgRoleSchema>;

// SPEC-08 / SPEC-11: this is the ONE parser for the member-side connection
// contract. Keep it strict: silently dropping a misspelled key turns an
// explicit sharing choice into a different contract.
export const connectionModes = ['shared', 'isolated'] as const;
export const connectionModeSchema = z.enum(connectionModes);
export type ConnectionMode = z.infer<typeof connectionModeSchema>;

export const connectionOverrideValues = ['share', 'withhold'] as const;
export const connectionOverrideValueSchema = z.enum(connectionOverrideValues);
export const connectionOverridesSchema = z
  .object({
    role_configs: connectionOverrideValueSchema.optional(),
    skills: connectionOverrideValueSchema.optional(),
    agent_settings: connectionOverrideValueSchema.optional(),
    learnings_visibility: connectionOverrideValueSchema.optional(),
  })
  .strict();
export type ConnectionOverrides = z.infer<typeof connectionOverridesSchema>;

// The four states are DERIVED from three timestamps (accepted_at, revoked_at,
// expires_at). 0162 ships no status column on purpose — a status is a second
// source of truth that a crashed handler can leave disagreeing with the
// timestamps, and timestamps cannot disagree with themselves. This enum is the
// wire spelling of that derivation, never a column.
export const orgInvitationStatuses = ['pending', 'expired', 'accepted', 'revoked'] as const;
export const orgInvitationStatusSchema = z.enum(orgInvitationStatuses);
export type OrgInvitationStatus = z.infer<typeof orgInvitationStatusSchema>;

// ── A1.2 / SPEC-11 §3.2.2 — where an invitation attaches somebody ───
//
// Three rungs and only three. Column/card attachment stays reachable through
// the manual grants API and is deliberately NOT offered on an invitation in v1
// (SPEC-11 §11.6, "keep the picker simple"). The gateway mirror of these
// vocabularies is `apps/chaos-gateway/src/lib/invitation-attachment.ts`, and
// the fence under both is migration 0350's CHECK constraints.
export const inviteAttachScopeSchema = z.enum(['org', 'project', 'board']);
export type InviteAttachScope = z.infer<typeof inviteAttachScopeSchema>;

export const inviteShareOverridesSchema = z
  .object({
    capabilities: z
      .object({ actions: z.array(z.string()).optional(), canWrite: z.boolean().optional() })
      .strict()
      .optional(),
    assets: z
      .object({
        kb_collections: z.enum(['org_granted', 'points_only', 'none']).optional(),
        comm: z.enum(['org_bound', 'points_only', 'none']).optional(),
        docs_mount: z.enum(['org', 'none']).optional(),
        org_chart: z.enum(['full', 'hidden']).optional(),
      })
      .strict()
      .optional(),
    items: z.array(z.unknown()).optional(),
  })
  .strict();
export type InviteShareOverrides = z.infer<typeof inviteShareOverridesSchema>;

/**
 * SPEC-11 §3.5 — the "What you get in «Org»" receipt, SERVER-COMPUTED.
 *
 * Counts and names appear ONLY for what is being GRANTED (SPEC-11 §7,
 * enumeration resistance): there is no "N hidden items" field here and there
 * must never be one, because a withheld catalogue is exactly what an agent
 * should not be able to read off a consent screen.
 */
export const invitationYouGetSchema = z.object({
  attachment: z.object({ scope: inviteAttachScopeSchema, label: z.string() }),
  capabilities: z.object({ canWrite: z.boolean(), brainSearch: z.boolean() }),
  kbCollections: z.object({ policy: z.enum(['org_granted', 'points_only', 'none']) }),
  comm: z.object({ policy: z.enum(['org_bound', 'points_only', 'none']) }),
  docs: z.boolean(),
  orgChart: z.boolean(),
});
export type InvitationYouGet = z.infer<typeof invitationYouGetSchema>;

export const orgInvitationSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  // The address the seat is reserved for. Visible to Workspace admins, who
  // typed it; the PUBLIC preview shape below carries only the mask.
  email: z.string(),
  maskedEmail: z.string(),
  orgRole: invitableOrgRoleSchema,
  status: orgInvitationStatusSchema,
  invitedByUserId: z.string().uuid(),
  invitedByName: z.string().nullable(),
  personalMessage: z.string().nullable(),
  // NULL is load-bearing: it means no mail left the building for this row and
  // the invitation travels by the copy-link the inviter was shown. It is not
  // "pending delivery".
  emailSentAt: z.string().datetime().nullable(),
  resendCount: z.number().int(),
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
  requestedMode: connectionModeSchema.nullable().optional(),
  requestedOverrides: connectionOverridesSchema.nullable().optional(),
  attachScope: inviteAttachScopeSchema.nullable().optional(),
  attachProjectId: z.string().uuid().nullable().optional(),
  attachBoardId: z.string().uuid().nullable().optional(),
  shareOverrides: inviteShareOverridesSchema.nullable().optional(),
  createdAt: z.string().datetime(),
});
export type OrgInvitation = z.infer<typeof orgInvitationSchema>;

export const createOrgInvitationSchema = z.object({
  email: z.string().min(3).max(254),
  role: invitableOrgRoleSchema,
  personalMessage: z.string().max(500).optional(),
  requestedMode: connectionModeSchema.optional(),
  requestedOverrides: connectionOverridesSchema.optional(),
  // A1.2 / SPEC-11 §3.2.2. All four optional and all four refused with 422
  // `invite_scope_not_storable` unless SHARE_PROFILES_ENABLED is on — the wire
  // shape exists ahead of the flag so a client can be written once.
  attachScope: inviteAttachScopeSchema.optional(),
  attachProjectId: z.string().uuid().optional(),
  attachBoardId: z.string().uuid().optional(),
  shareOverrides: inviteShareOverridesSchema.optional(),
}).strict();
export type CreateOrgInvitationInput = z.infer<typeof createOrgInvitationSchema>;

/** What actually happened to the mail. `emailed:false` is reported, never hidden. */
export const invitationDeliverySchema = z.object({
  emailed: z.boolean(),
  provider: z.string(),
  /** machine-readable reason when emailed is false, e.g. `mailer_not_configured`. */
  reason: z.string().optional(),
});
export type InvitationDelivery = z.infer<typeof invitationDeliverySchema>;

/**
 * The create/resend response. `acceptUrl` is the ONLY time the raw link is ever
 * visible: the table stores its sha256, so the list endpoint structurally
 * cannot show it again. Resending mints a NEW link and kills the old one.
 */
export const createdOrgInvitationSchema = z.object({
  invitation: orgInvitationSchema,
  acceptUrl: z.string(),
  delivery: invitationDeliverySchema,
});
export type CreatedOrgInvitation = z.infer<typeof createdOrgInvitationSchema>;

/**
 * What the PUBLIC preview endpoint shows the holder of a link. Deliberately
 * minimal, and the address is masked: a forwarded link must not also hand over
 * somebody's email. Nothing here identifies the Workspace's other members, its
 * id, or the inviter's address.
 */
export const invitationPreviewSchema = z.object({
  orgName: z.string(),
  orgRole: invitableOrgRoleSchema,
  maskedEmail: z.string(),
  inviterName: z.string().nullable(),
  personalMessage: z.string().nullable(),
  expiresAt: z.string().datetime(),
  /** true → the address already has a CHAOS account and must sign in first. */
  requiresSignIn: z.boolean(),
  consentVersion: z.string(),
  consentDocument: z
    .object({
      version: z.string(),
      bodyMd: z.string(),
      retains: z.unknown(),
      leavesWithYou: z.unknown(),
      modes: z.unknown(),
    })
    .optional(),
  connectionProfile: z
    .object({
      defaultMode: z.literal('isolated'),
      requestedMode: connectionModeSchema.nullable(),
      requestedOverrides: connectionOverridesSchema.nullable(),
    })
    .optional(),
  /**
   * A1.2 — flag-gated and ABSENT-TOLERANT, and both words are the contract.
   * The gateway omits the key entirely when SHARE_PROFILES_ENABLED is off; the
   * join page must hide the panel rather than render an empty one, so that a
   * UI built for the flag can ship before the flag is raised (SPEC-11 §6.2:
   * «Absent `youGet` field ⇒ panel hidden (flag-off tolerance)»).
   */
  youGet: invitationYouGetSchema.optional(),
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

export const acceptInvitationSchema = z.object({
  token: z.string().min(1),
  /** Required only when the address has no account yet. */
  password: z.string().optional(),
  displayName: z.string().max(120).optional(),
  mode: connectionModeSchema.optional(),
  overrides: connectionOverridesSchema.optional(),
}).strict();
export type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>;

export const acceptInvitationResultSchema = z.object({
  ok: z.literal(true),
  orgId: z.string().uuid(),
  orgName: z.string(),
  orgRole: invitableOrgRoleSchema,
  accountCreated: z.boolean(),
  email: z.string(),
  credentialGrantsRevoked: z.number().int(),
  publicationsRevoked: z.number().int(),
  connectionMode: connectionModeSchema.optional(),
});
export type AcceptInvitationResult = z.infer<typeof acceptInvitationResultSchema>;

// ── Constitution (per-org, versioned) ────────────────────────────────
export const constitutionSchema = z.object({
  orgId: z.string().uuid(),
  currentVersion: z.number().int().positive(),
  body: z.string(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string().uuid().nullable(),
});
export type Constitution = z.infer<typeof constitutionSchema>;

export const updateConstitutionSchema = z.object({
  body: z.string().min(1).max(1_000_000),
  changeNote: z.string().max(1000).nullable().optional(),
});
export type UpdateConstitutionInput = z.infer<typeof updateConstitutionSchema>;

export const constitutionHistorySchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  version: z.number().int().positive(),
  body: z.string(),
  changedBy: z.string().uuid().nullable(),
  changeNote: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type ConstitutionHistory = z.infer<typeof constitutionHistorySchema>;

// ── KB Shared Libraries (Org Architecture Phase B) ───────────────────
// An ACCOUNT OWNER curates "shared libraries" (collections) of KB material and
// grants a library to an organization / department / role-type. Mirrors
// chaos.kb_collections + chaos.kb_collection_grants (schema.ts → kbCollections,
// kbCollectionGrants) and migration 0057_kb_shared_libraries.sql. All behaviour
// is flag-gated (KB_SHARED_LIBRARIES_ENABLED, default OFF) on the retrieval
// path; the management API below is owner-gated regardless of the flag.

// grantee kinds (mirrors kb_collection_grants_grantee_type_check).
export const kbGranteeTypes = ['organization', 'department', 'role'] as const;
export const kbGranteeTypeSchema = z.enum(kbGranteeTypes);
export type KbGranteeType = z.infer<typeof kbGranteeTypeSchema>;

export const kbCollectionSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  name: z.string().min(1).max(200),
  topic: z.string().nullable(),
  kind: z.string().nullable(),
  createdAt: z.string().datetime().nullable(),
});
export type KbCollection = z.infer<typeof kbCollectionSchema>;

export const createKbCollectionSchema = z.object({
  name: z.string().min(1).max(200),
  topic: z.string().max(500).nullable().optional(),
  kind: z.string().max(60).optional(),
});
export type CreateKbCollectionInput = z.infer<typeof createKbCollectionSchema>;

// add / remove KB sources to a collection (the owner-gated WRITE path).
export const kbCollectionMaterialsSchema = z.object({
  sourceIds: z.array(z.string().uuid()).min(1).max(1000),
});
export type KbCollectionMaterialsInput = z.infer<typeof kbCollectionMaterialsSchema>;

export const kbCollectionGrantSchema = z.object({
  id: z.string().uuid(),
  collectionId: z.string().uuid(),
  granteeType: kbGranteeTypeSchema,
  granteeId: z.string().uuid(),
  createdAt: z.string().datetime().nullable(),
});
export type KbCollectionGrant = z.infer<typeof kbCollectionGrantSchema>;

export const createKbGrantSchema = z.object({
  granteeType: kbGranteeTypeSchema,
  granteeId: z.string().uuid(),
});
export type CreateKbGrantInput = z.infer<typeof createKbGrantSchema>;

// ── KB Promotion Pipeline (Org Architecture Phase C) ─────────────────
// An agent / the Conductor / a user PROPOSES promoting a private learning into
// a Phase-B shared library; the founder (account owner) approves; applying
// replays the Phase-B primitive (link source → kb_collection + grant). Mirrors
// chaos.kb_promotion_proposals + the new kb_sources columns
// (classification / promotion_status / proposed_by) and migration
// 0058_kb_promotion_pipeline.sql. All behaviour flag-gated
// (PROMOTION_PIPELINE_ENABLED, default OFF).

// who proposed a promotion (mirrors *_proposed_by_check).
export const promotionProposers = ['agent', 'conductor', 'user'] as const;
export const promotionProposerSchema = z.enum(promotionProposers);
export type PromotionProposer = z.infer<typeof promotionProposerSchema>;

// the sanitization signal (mirrors kb_sources_classification_check).
export const kbClassifications = ['general', 'organization_specific'] as const;
export const kbClassificationSchema = z.enum(kbClassifications);
export type KbClassification = z.infer<typeof kbClassificationSchema>;

// per-source promotion lifecycle (mirrors kb_sources_promotion_status_check).
export const promotionStatuses = [
  'none',
  'pending',
  'kept_local',
  'promoted',
  'discarded',
] as const;
export const promotionStatusSchema = z.enum(promotionStatuses);
export type PromotionStatus = z.infer<typeof promotionStatusSchema>;

// proposal status machine (mirrors kb_promotion_proposals_status_check).
export const promotionProposalStatuses = [
  'pending',
  'approved',
  'rejected',
  'applied',
] as const;
export const promotionProposalStatusSchema = z.enum(promotionProposalStatuses);
export type PromotionProposalStatus = z.infer<
  typeof promotionProposalStatusSchema
>;

// the intended Phase-B write a proposal records (replayed on apply): a target
// collection (existing id OR a name to create) + grants to org|dept|role.
export const promotionTargetGrantSchema = z.object({
  granteeType: kbGranteeTypeSchema,
  granteeId: z.string().uuid(),
});
export type PromotionTargetGrant = z.infer<typeof promotionTargetGrantSchema>;

export const promotionTargetSchema = z.object({
  // promote into this existing collection (omit to create one from name).
  collectionId: z.string().uuid().nullable().optional(),
  // create a collection with this name when collectionId is absent.
  collectionName: z.string().min(1).max(200).nullable().optional(),
  grants: z.array(promotionTargetGrantSchema).max(50).optional(),
});
export type PromotionTarget = z.infer<typeof promotionTargetSchema>;

// the full proposal row, wire shape (snake→camel; ISO strings).
export const kbPromotionProposalSchema = z.object({
  id: z.string().uuid(),
  sourceId: z.string().uuid(),
  accountId: z.string().uuid(),
  originOrgId: z.string().uuid().nullable(),
  proposedBy: promotionProposerSchema.nullable(),
  proposedByUserId: z.string().uuid().nullable(),
  proposedByRoleId: z.string().uuid().nullable(),
  proposedClassification: kbClassificationSchema.nullable(),
  proposedTarget: promotionTargetSchema,
  rationale: z.string().nullable(),
  sanitizationFlags: z.array(z.unknown()),
  status: promotionProposalStatusSchema,
  reviewedBy: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  appliedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime().nullable(),
});
export type KbPromotionProposal = z.infer<typeof kbPromotionProposalSchema>;

// create-proposal body (owner/founder OR Conductor). The proposer kind is set
// server-side from the auth context, not trusted from the body.
export const createPromotionProposalSchema = z.object({
  sourceId: z.string().uuid(),
  proposedClassification: kbClassificationSchema.optional(),
  proposedTarget: promotionTargetSchema.optional(),
  rationale: z.string().max(4000).nullable().optional(),
  sanitizationFlags: z.array(z.unknown()).max(200).optional(),
  originOrgId: z.string().uuid().nullable().optional(),
});
export type CreatePromotionProposalInput = z.infer<
  typeof createPromotionProposalSchema
>;

// apply (promote) / keep-local / discard a proposal. promote may override the
// target the proposal recorded (founder edits before applying).
export const promotionDecisionSchema = z.object({
  proposalId: z.string().uuid(),
  // promote = apply the Phase-B write; keep_local / discard = terminal no-share.
  decision: z.enum(['promote', 'keep_local', 'discard']),
  // optional founder override of the recorded target on promote.
  target: promotionTargetSchema.optional(),
});
export type PromotionDecisionInput = z.infer<typeof promotionDecisionSchema>;

// ── Cross-Org Config Copy (Org Architecture Phase D) ─────────────────
// The account owner takes a SOURCE config object (a role / department /
// division) in one org and "Applies it to organizations" — creating an
// INDEPENDENT COPY (a SNAPSHOT, not a live link) in each OTHER org the SAME
// account owns. APPLY = REUSE the existing create primitive once per target org
// (the Phase-C doctrine, applied to org structure). Mirrors chaos.config_copy_jobs
// + the copied_from_id/copied_from_org_id provenance columns
// (schema.ts → configCopyJobs / divisions|departments|roles) and migration
// 0059_cross_org_config_copy.sql. All behaviour is flag-gated
// (CROSS_ORG_COPY_ENABLED, default OFF); every route 404s when OFF.

// the copyable config object kinds (mirrors config_copy_jobs_object_type_check).
export const configCopyObjectTypes = ['role', 'department', 'division'] as const;
export const configCopyObjectTypeSchema = z.enum(configCopyObjectTypes);
export type ConfigCopyObjectType = z.infer<typeof configCopyObjectTypeSchema>;

// on-conflict policy for a same-slug collision in a target org (mirrors
// config_copy_jobs_conflict_policy_check). 'skip' = report the conflict, create
// nothing in that org (least-surprising default). 'suffix' = create a clearly
// suffixed copy ("<slug>-copy", "<slug>-copy-2", …).
export const configCopyConflictPolicies = ['skip', 'suffix'] as const;
export const configCopyConflictPolicySchema = z.enum(configCopyConflictPolicies);
export type ConfigCopyConflictPolicy = z.infer<
  typeof configCopyConflictPolicySchema
>;

// per-target outcome (one entry per requested target org).
export const configCopyResultStatuses = [
  'created',
  'skipped_conflict',
  'error',
] as const;
export const configCopyResultStatusSchema = z.enum(configCopyResultStatuses);
export type ConfigCopyResultStatus = z.infer<
  typeof configCopyResultStatusSchema
>;

export const configCopyResultSchema = z.object({
  orgId: z.string().uuid(),
  status: configCopyResultStatusSchema,
  // the id of the newly-created copy (present only when status === 'created').
  newId: z.string().uuid().nullable(),
  // the suffixed slug actually used (present when a suffix was applied).
  slug: z.string().nullable().optional(),
  // why a target was skipped / errored (human-readable).
  reason: z.string().nullable(),
});
export type ConfigCopyResult = z.infer<typeof configCopyResultSchema>;

// request body for POST /api/account/config-copy. The source object + its org
// are identified; the target org list is the founder's checklist selection. The
// proposer/account is resolved server-side from auth, never trusted from body.
export const configCopyRequestSchema = z.object({
  objectType: configCopyObjectTypeSchema,
  sourceObjectId: z.string().uuid(),
  sourceOrgId: z.string().uuid(),
  // the OTHER orgs to copy into (the source org is rejected if included — a
  // copy into the source org is a no-op the API filters out).
  targetOrgIds: z.array(z.string().uuid()).min(1).max(200),
  // default 'skip' (least-surprising): a same-slug collision reports a conflict
  // and creates nothing in that org.
  conflictPolicy: configCopyConflictPolicySchema.default('skip'),
});
export type ConfigCopyRequestInput = z.infer<typeof configCopyRequestSchema>;

// the recorded audit row (snake→camel; ISO strings).
export const configCopyJobSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  objectType: configCopyObjectTypeSchema,
  sourceObjectId: z.string().uuid(),
  sourceOrgId: z.string().uuid().nullable(),
  targetOrgIds: z.array(z.string().uuid()),
  conflictPolicy: configCopyConflictPolicySchema,
  results: z.array(configCopyResultSchema),
  requestedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime().nullable(),
});
export type ConfigCopyJob = z.infer<typeof configCopyJobSchema>;

// ── Guided Org Teardown (Org Architecture Phase E) ───────────────────
// The account owner reviews a per-entity-type breakdown of an org, chooses
// keep|delete per type, confirms explicitly, and the org's LOCAL data is torn
// down in dependency order — WITHOUT ever touching ACCOUNT-OWNED assets
// (kb_collections, config_copy_jobs, kb_promotion_proposals, accounts), which
// are only DETACHED (grants revoked / org pointers NULLed). Mirrors
// chaos.org_teardown_jobs + migration 0060_org_teardown.sql. All behaviour is
// flag-gated (ORG_TEARDOWN_ENABLED, default OFF); every route 404s when OFF and
// the existing soft-archive DELETE /api/orgs/:orgId is byte-identical.

// ── THE org-teardown entity table. ONE list, not three. ───────────────
// Until 2026-08-06 the same thirteen entity types were written out THREE
// times: this array, ENTITY_LABELS/ENTITY_ORDER in org-teardown.ts, and the
// TBL relation map in the same file. Nothing compared them to each other and
// nothing compared any of them to the database, so a table joining the schema
// with an org_id was in none of them: not counted in the preview the founder
// confirms, not deleted, not mentioned in the result — which still said
// `completed`. The labels, the order and the relation names now all come from
// here, and packages/db/tenancy/org-scope.tsv (checked against the live
// catalog on every preview and every execute) is what ties the list to the
// schema.
//
// These are the org-LOCAL entity types the founder may choose to keep|delete.
// Account-owned assets are NOT here — a teardown never deletes them.
//
//   type      the wire value (keep|delete choices, preview lines).
//   label     the human string shown in the preview.
//   relation  the schema-qualified relation the count and the delete address.
//   scope     'org' = the relation carries org_id; 'board' = it is reached
//             through boards.org_id (chaos.columns has no org column).
export const orgTeardownEntities = [
  { type: 'projects', label: 'Projects', relation: 'chaos.projects', scope: 'org' },
  { type: 'boards', label: 'Boards', relation: 'chaos.boards', scope: 'org' },
  { type: 'columns', label: 'Columns', relation: 'chaos.columns', scope: 'board' },
  { type: 'cards', label: 'Cards', relation: 'chaos.cards', scope: 'org' },
  { type: 'sessions', label: 'Sessions', relation: 'chaos.sessions', scope: 'org' },
  { type: 'divisions', label: 'Divisions', relation: 'chaos.divisions', scope: 'org' },
  { type: 'departments', label: 'Departments', relation: 'chaos.departments', scope: 'org' },
  { type: 'roles', label: 'Roles', relation: 'chaos.roles', scope: 'org' },
  {
    type: 'kbSources',
    label: 'Knowledge sources (org-scoped)',
    relation: 'chaos.kb_sources',
    scope: 'org',
  },
  { type: 'constitution', label: 'Constitution', relation: 'chaos.constitutions', scope: 'org' },
  { type: 'approvalRules', label: 'Approval rules', relation: 'chaos.approval_rules', scope: 'org' },
  {
    type: 'columnTriggers',
    label: 'Column triggers (watchers)',
    relation: 'chaos.column_triggers',
    scope: 'org',
  },
  { type: 'members', label: 'Members', relation: 'chaos.org_memberships', scope: 'org' },
] as const;

export type OrgTeardownEntity = (typeof orgTeardownEntities)[number];
export type OrgTeardownEntityType = OrgTeardownEntity['type'];

// derived — z.enum needs a non-empty literal tuple, which `.map` cannot carry
// in the type system. The VALUES are derived; only the tuple shape is asserted.
export const orgTeardownEntityTypes = orgTeardownEntities.map((e) => e.type) as unknown as [
  OrgTeardownEntityType,
  ...OrgTeardownEntityType[],
];
export const orgTeardownEntityTypeSchema = z.enum(orgTeardownEntityTypes);

/** Label for an entity type, from the one table above. */
export const orgTeardownEntityLabels = Object.fromEntries(
  orgTeardownEntities.map((e) => [e.type, e.label]),
) as Record<OrgTeardownEntityType, string>;

/** Schema-qualified relation for an entity type, from the one table above. */
export const orgTeardownEntityRelations = Object.fromEntries(
  orgTeardownEntities.map((e) => [e.type, e.relation]),
) as Record<OrgTeardownEntityType, string>;

// per-type keep|delete choice.
export const orgTeardownChoiceSchema = z.enum(['keep', 'delete']);
export type OrgTeardownChoice = z.infer<typeof orgTeardownChoiceSchema>;

// PREVIEW — a single line in the per-entity-type count breakdown.
export const orgTeardownCountLineSchema = z.object({
  entityType: orgTeardownEntityTypeSchema,
  // human label for the UI (e.g. "Cards").
  label: z.string(),
  count: z.number().int().nonnegative(),
  // every org-local entity defaults to delete; the disposition is always
  // 'will-delete' for these lines (the founder may flip individual types to
  // keep in the teardown request).
  disposition: z.literal('will-delete'),
  // optional 'depends-on' note: a hard FK dependency the UI should warn about.
  // E.g. boards/columns/cards CASCADE off projects.project_id — deleting
  // `projects` ALSO removes them, so KEEPING them while DELETING projects is an
  // illegal combination (the teardown rejects it). When present, the UI should
  // surface `note` and, if `requires` is set, gate the keep|delete choice so it
  // can't contradict the named parent (see ORG_TEARDOWN_KEEP_CONFLICT).
  dependsOn: z
    .object({
      // the parent entity type this line CASCADE-depends on (e.g. 'projects').
      requires: orgTeardownEntityTypeSchema,
      note: z.string(),
    })
    .optional(),
});
export type OrgTeardownCountLine = z.infer<typeof orgTeardownCountLineSchema>;

// PREVIEW — a single line in the DEPENDENCIES section: things OUTSIDE the org
// or ACCOUNT-OWNED. Marked will-keep (account-owned, preserved) or will-detach
// (external ref nulled / grant revoked). Never will-delete.
export const orgTeardownDependencyDispositionSchema = z.enum([
  'will-keep', // account-owned asset, preserved untouched (only its org link detaches)
  'will-detach', // external reference detached (grant revoked / provenance pointer nulled)
]);
export type OrgTeardownDependencyDisposition = z.infer<
  typeof orgTeardownDependencyDispositionSchema
>;
export const orgTeardownDependencyLineSchema = z.object({
  // stable key (e.g. 'kbCollectionGrants', 'kbPromotionProposals').
  key: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
  disposition: orgTeardownDependencyDispositionSchema,
  // one-line explanation surfaced to the founder before they confirm.
  note: z.string(),
});
export type OrgTeardownDependencyLine = z.infer<
  typeof orgTeardownDependencyLineSchema
>;

// PREVIEW / RESULT — one relation the teardown deletes that no entity-type
// line names. These come from packages/db/tenancy/org-scope.tsv (class
// `delete`, not one of the thirteen entity relations): the Workspace's
// knowledge base, its approvals, its notification deliveries, its trash, its
// task queue, its external file links, its profiles, its calls, its comm
// channel. They used to be invisible in both directions — absent from the
// preview the founder confirms AND absent from the result he reads afterwards.
export const orgTeardownPurgeLineSchema = z.object({
  // schema-qualified relation, e.g. 'chaos.kb_chunks'.
  relation: z.string(),
  count: z.number().int().nonnegative(),
  // the manifest's own note: WHY this class, in the founder's terms.
  note: z.string().default(''),
});
export type OrgTeardownPurgeLine = z.infer<typeof orgTeardownPurgeLineSchema>;

// PREVIEW response.
export const orgTeardownPreviewSchema = z.object({
  orgId: z.string().uuid(),
  orgSlug: z.string(),
  orgName: z.string(),
  // per-entity-type counts of org-local data (all will-delete).
  counts: z.array(orgTeardownCountLineSchema),
  // external + account-owned references (will-keep / will-detach).
  dependencies: z.array(orgTeardownDependencyLineSchema),
  // ALSO will-delete, on top of `counts`. Defaulted so an older gateway that
  // does not send the field still parses — but a gateway that deletes these
  // rows without sending it is the exact silence this array exists to end.
  purges: z.array(orgTeardownPurgeLineSchema).default([]),
});
export type OrgTeardownPreview = z.infer<typeof orgTeardownPreviewSchema>;

// TEARDOWN request body. The founder confirms with the org's exact slug
// (typed-to-confirm) and supplies per-type keep|delete choices. Any type
// omitted defaults to 'delete' (the safe full-teardown default).
export const orgTeardownRequestSchema = z.object({
  // must equal the target org's slug — a typed-to-confirm guard.
  confirmSlug: z.string().min(1),
  // per-type choices; omitted types default to 'delete'.
  choices: z.record(orgTeardownEntityTypeSchema, orgTeardownChoiceSchema).default({}),
});
export type OrgTeardownRequestInput = z.infer<typeof orgTeardownRequestSchema>;

// ── keep/delete CASCADE-conflict guard ───────────────────────────────
// boards/columns/cards each carry `project_id … ON DELETE CASCADE` (migration
// 0003 / 0000 snapshot). So deleting `projects` ALSO destroys their boards,
// columns and cards regardless of the keep choice. Choosing delete(projects)
// while keep(boards|columns|cards) is therefore CONTRADICTORY: the keep would
// be silently violated by the cascade. We REJECT that combination up-front
// (atomic — nothing is deleted) rather than silently widening the delete.
//
// Map: child entity type → parent it CASCADE-depends on.
export const orgTeardownCascadeParents: Partial<
  Record<OrgTeardownEntityType, OrgTeardownEntityType>
> = {
  boards: 'projects',
  columns: 'projects',
  cards: 'projects',
};

// stable error code surfaced to the client (mapped to HTTP 409 by the route).
export const ORG_TEARDOWN_KEEP_CONFLICT = 'teardown_keep_conflict' as const;

// Compute keep/delete-cascade conflicts for a choice set. Returns the list of
// child types that are set to KEEP while their CASCADE parent is set to DELETE
// (omitted types default to 'delete'). Empty array = no conflict. Pure — no DB.
export function orgTeardownKeepConflicts(
  choices: Partial<Record<OrgTeardownEntityType, OrgTeardownChoice>>,
): Array<{ child: OrgTeardownEntityType; parent: OrgTeardownEntityType }> {
  const eff = (t: OrgTeardownEntityType): OrgTeardownChoice => choices[t] ?? 'delete';
  const conflicts: Array<{ child: OrgTeardownEntityType; parent: OrgTeardownEntityType }> = [];
  for (const [child, parent] of Object.entries(orgTeardownCascadeParents) as Array<
    [OrgTeardownEntityType, OrgTeardownEntityType]
  >) {
    if (eff(parent) === 'delete' && eff(child) === 'keep') {
      conflicts.push({ child, parent });
    }
  }
  return conflicts;
}

// the detached-external summary (account-owned rows are preserved; only their
// org links are revoked/nulled).
export const orgTeardownDetachedSchema = z.object({
  grantsRevoked: z.number().int().nonnegative(),
  kbSourcesDetached: z.number().int().nonnegative(),
  promotionProposalsDetached: z.number().int().nonnegative(),
  configCopyJobsDetached: z.number().int().nonnegative(),
  copiedFromPointersCleared: z.number().int().nonnegative(),
  // credentials — the executor has reported these since the vault audit; the
  // schema simply never named them, which is how a number becomes a rumour.
  credentialGrantsRevoked: z.number().int().nonnegative().default(0),
  credentialSharesRemoved: z.number().int().nonnegative().default(0),
  credentialsDeleted: z.number().int().nonnegative().default(0),
  // the three ON DELETE NO ACTION pointers. Before 2026-08-06 a row holding any
  // of them did not leave residue — it aborted the whole org delete with a
  // 23503. The pointing row is not this Workspace's content, so it survives and
  // only the pointer is cleared; these are the counts of that.
  commChannelDefaultsCleared: z.number().int().nonnegative().default(0),
  roleAdditionOriginsCleared: z.number().int().nonnegative().default(0),
  configCopyTargetsCleared: z.number().int().nonnegative().default(0),
});
export type OrgTeardownDetached = z.infer<typeof orgTeardownDetachedSchema>;

// TEARDOWN result — the summary of deleted vs kept + the recorded audit row id.
export const orgTeardownResultStatuses = ['completed', 'partial', 'failed'] as const;
export const orgTeardownResultStatusSchema = z.enum(orgTeardownResultStatuses);
export type OrgTeardownResultStatus = z.infer<
  typeof orgTeardownResultStatusSchema
>;
export const orgTeardownResultSchema = z.object({
  jobId: z.string().uuid().nullable(),
  orgId: z.string().uuid(),
  status: orgTeardownResultStatusSchema,
  // true when the org row itself was hard-deleted (false on a partial teardown).
  orgDeleted: z.boolean(),
  // per-type rows actually deleted.
  deleted: z.record(orgTeardownEntityTypeSchema, z.number().int().nonnegative()),
  // per-type kept (chosen 'keep'); empty on a full teardown.
  kept: z.array(orgTeardownEntityTypeSchema),
  detached: orgTeardownDetachedSchema,
  // relations swept from packages/db/tenancy/org-scope.tsv on a FULL teardown,
  // with the rows actually removed. The mirror of `purges` in the preview: what
  // was promised, and what was done.
  purged: z.array(orgTeardownPurgeLineSchema).default([]),
});
export type OrgTeardownResult = z.infer<typeof orgTeardownResultSchema>;

// the recorded audit row (snake→camel; ISO strings).
export const orgTeardownJobSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  orgId: z.string().uuid(),
  orgSlug: z.string().nullable(),
  orgName: z.string().nullable(),
  choices: z.record(z.string(), z.string()),
  deleted: z.record(z.string(), z.number()),
  detached: z.record(z.string(), z.number()),
  orgDeleted: z.boolean(),
  status: orgTeardownResultStatusSchema,
  requestedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime().nullable(),
});
export type OrgTeardownJob = z.infer<typeof orgTeardownJobSchema>;

// ── Scoped Access (Org Architecture Phase G) ─────────────────────────
// A grantee-typed, account-scoped grant that narrows a member/agent's reach to
// a granted Organization-or-Department subtree (inherited down to
// projects/boards), with per-board override possible at the resolver. The three
// §3 axes: SCOPE = level + scope target; ACTIONS = capabilities.actions[] +
// canWrite; AUDIT = access_audit_log (always-on). §5 On-Behalf-Of: an AGENT
// grant carries inviterUserId — the agent's effective reach is INTERSECTED with
// that user's reach and can never exceed it. Mirrors chaos.access_grants +
// chaos.access_audit_log (schema.ts → accessGrants, accessAuditLog) and
// migration 0062_scoped_access.sql. All ENFORCEMENT is flag-gated
// (SCOPED_ACCESS_ENABLED, default OFF); the management API is owner/admin-gated
// regardless of the flag.

// who a grant is about.
export const accessSubjectKinds = ['human', 'agent'] as const;
export const accessSubjectKindSchema = z.enum(accessSubjectKinds);
export type AccessSubjectKind = z.infer<typeof accessSubjectKindSchema>;

// the scope level a grant pins. The FULL access ladder (migrations 0063 + 0095):
//   organization ⊇ department ⊇ project ⊇ board ⊇ column ⊇ card
// A grant at any rung reaches everything at and below it (inheritance); a
// narrower grant on a specific project/board/column/card refines reach
// (override). Cards carry project_id (always) + board_id (nullable) + column_id
// (always) + id, so project/board/column/card grants are enforceable straight
// from the card row.
export const accessLevels = ['organization', 'department', 'project', 'board', 'column', 'card'] as const;
export const accessLevelSchema = z.enum(accessLevels);
export type AccessLevel = z.infer<typeof accessLevelSchema>;

// the ACTIONS axis: the card verbs a subject may invoke + a coarse write toggle.
// Values are validated against permissions_catalog at every writer and by the
// database trigger. They stay strings on the wire because the founder-managed
// catalog is extensible; runtime validation turns them into catalog actions.
export const accessCapabilitiesSchema = z.object({
  actions: z.array(z.string()).default(['card.read']),
  canWrite: z.boolean().default(false),
});
export type AccessCapabilities = z.infer<typeof accessCapabilitiesSchema>;

// the canonical read-only default capabilities for a brand-new agent (§4).
// P8a — kb.search is added to the default READ action set so that, once the
// enforcement flag (KB_LEVEL_SCOPE_ENABLED) is flipped ON, every org agent's
// lazy default grant (org/dept-wide) carries the KB READ verb at org scope and
// therefore sees the org-general KB layer. This is INERT while the flag is OFF
// (the resolver only consults grants on the scoped-retrieval path; the default
// grant capabilities are never read on the legacy path). kb.write is NOT added
// — writes stay an explicit, separately-granted capability.
export const ACCESS_DEFAULT_READONLY: AccessCapabilities = {
  actions: ['card.read', 'kb.search'],
  canWrite: false,
};

export function capabilitiesAllowAction(caps: AccessCapabilities, action: string): boolean {
  return caps.actions.includes(action);
}

export function intersectActions(a: string[], b: string[]): string[] {
  const bSet = new Set(b);
  return a.filter((x) => bSet.has(x));
}

export const accessGrantSchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  subjectKind: accessSubjectKindSchema,
  subjectId: z.string().uuid(),
  level: accessLevelSchema,
  scopeOrgId: z.string().uuid().nullable(),
  scopeDepartmentId: z.string().uuid().nullable(),
  // migration 0063 — project/board scope targets (nullable; exactly one of the
  // scope_* fields is set per row, matching `level`).
  scopeProjectId: z.string().uuid().nullable(),
  scopeBoardId: z.string().uuid().nullable(),
  // migration 0095 — column/card scope targets (nullable; exactly one of the
  // six scope_* fields is set per row, matching `level`).
  scopeColumnId: z.string().uuid().nullable(),
  scopeCardId: z.string().uuid().nullable(),
  capabilities: accessCapabilitiesSchema,
  inviterUserId: z.string().uuid().nullable(),
  grantedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime().nullable(),
  // migration 0095 — temporary/delegated grant lifecycle. All optional so a
  // pre-0095 row (none exist today) still parses. `delegated=true` ⇒ minted by
  // a non-admin in-session delegation (read-only, bounded); `expiresAt` is the
  // wall-clock TTL (null = permanent); `sessionId` binds the grant to a live
  // session (expires when the session dies). The Access UI uses these to badge
  // "Permanent (admin)" vs "Temporary — expires in …" and to drive the live
  // countdown + Revoke in the delegation tray (P6 §6.2/§6.4).
  delegated: z.boolean().optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  sessionId: z.string().uuid().nullable().optional(),
});
export type AccessGrant = z.infer<typeof accessGrantSchema>;

// ── P6 §6.2 — in-session DELEGATION payload (a can_delegate human lends an
// AGENT a TEMPORARY, READ-ONLY grant ⊆ the human's own live access). Mirrors
// the gateway delegateSchema in access-grants.ts. At least one of sessionId /
// ttlSeconds is required (the delegated⇒bounded DB invariant). capabilities are
// forced read-only server-side; the UI never offers a write toggle here.
export const delegateGrantSchema = z
  .object({
    // the AGENT (role) receiving the grant — delegation targets agents only.
    subjectId: z.string().uuid(),
    level: accessLevelSchema,
    scopeOrgId: z.string().uuid().nullable().optional(),
    scopeDepartmentId: z.string().uuid().nullable().optional(),
    scopeProjectId: z.string().uuid().nullable().optional(),
    scopeBoardId: z.string().uuid().nullable().optional(),
    scopeColumnId: z.string().uuid().nullable().optional(),
    scopeCardId: z.string().uuid().nullable().optional(),
    capabilities: accessCapabilitiesSchema.optional(),
    // bounded lifetime: at least one required (validated server-side too).
    sessionId: z.string().uuid().nullable().optional(),
    ttlSeconds: z.number().int().positive().nullable().optional(),
  })
  .refine((v) => !!v.sessionId || !!v.ttlSeconds, {
    message: 'a delegated grant must be bounded: provide sessionId and/or ttlSeconds',
  });
export type DelegateGrantInput = z.infer<typeof delegateGrantSchema>;

// create/revoke payloads (owner/admin-gated API). Exactly ONE of the four
// scope_* fields is required, matching `level` (validated server-side too).
export const createAccessGrantSchema = z
  .object({
    subjectKind: accessSubjectKindSchema,
    subjectId: z.string().uuid(),
    level: accessLevelSchema,
    scopeOrgId: z.string().uuid().nullable().optional(),
    scopeDepartmentId: z.string().uuid().nullable().optional(),
    // migration 0063 — project/board scope targets.
    scopeProjectId: z.string().uuid().nullable().optional(),
    scopeBoardId: z.string().uuid().nullable().optional(),
    // migration 0095 — column/card scope targets.
    scopeColumnId: z.string().uuid().nullable().optional(),
    scopeCardId: z.string().uuid().nullable().optional(),
    capabilities: accessCapabilitiesSchema.optional(),
    // §5: present only for agent grants connected by a colleague (the cap).
    inviterUserId: z.string().uuid().nullable().optional(),
  })
  .refine(
    (v) =>
      v.level === 'organization'
        ? !!v.scopeOrgId
        : v.level === 'department'
          ? !!v.scopeDepartmentId
          : v.level === 'project'
            ? !!v.scopeProjectId
            : v.level === 'board'
              ? !!v.scopeBoardId
              : v.level === 'column'
                ? !!v.scopeColumnId
                : !!v.scopeCardId,
    { message: 'scope target must match level' },
  );
export type CreateAccessGrantInput = z.infer<typeof createAccessGrantSchema>;

// ── Effective-access viewer wire shape (§6) ──────────────────────────
// "What can a member/agent actually reach, and why?" — a flattened, explained
// view assembled by the resolver: each reachable scope + the grant it came
// from (inherited from a dept, or a direct org grant, or — for agents — capped
// by the inviter). enforced=false means the flag is OFF (legacy reach shown).
export const effectiveAccessScopeSchema = z.object({
  level: accessLevelSchema,
  scopeOrgId: z.string().uuid().nullable(),
  scopeDepartmentId: z.string().uuid().nullable(),
  // migration 0063 — the project/board this scope pins (when level is
  // project/board). Null for org/department scopes.
  scopeProjectId: z.string().uuid().nullable(),
  scopeBoardId: z.string().uuid().nullable(),
  // migration 0095 — the column/card this scope pins (when level is
  // column/card). Null for org/department/project/board scopes.
  scopeColumnId: z.string().uuid().nullable(),
  scopeCardId: z.string().uuid().nullable(),
  // The org this scope resolves to, regardless of level, so the enforcement
  // seam can match ANY grant against the acting org with one field. For an
  // organization-level scope this equals scopeOrgId; for department/project/
  // board/column/card scopes it is the owning org resolved via the
  // dept/project/board/column/card row. (Historically named scopeDeptOrgId for
  // the department arm; now carries the owning org for all narrower levels.)
  scopeDeptOrgId: z.string().uuid().nullable(),
  // The project this scope resolves to, so a board/column/card scope (and a
  // project scope) can be matched against a card's project_id. For a
  // project-level scope this equals scopeProjectId; for board/column/card scopes
  // it is the owning project resolved via the board/column/card row. Null for
  // org/department scopes (they inherit ALL projects).
  scopeResolvedProjectId: z.string().uuid().nullable(),
  // migration 0095 — the board this scope resolves to, so a column/card scope
  // can be matched against a card's board_id and a board inviter can subsume a
  // narrower column/card scope under it. For a board-level scope this equals
  // scopeBoardId; for a column scope it is the column's owning board; for a card
  // scope it is the card's owning board (nullable on legacy cards). Null for
  // org/department/project scopes (they inherit ALL boards).
  scopeResolvedBoardId: z.string().uuid().nullable(),
  // migration 0095 — the column this scope resolves to, so a card scope can be
  // matched against a card's column_id and a column inviter can subsume a card
  // scope under it. For a column-level scope this equals scopeColumnId; for a
  // card scope it is the card's owning column. Null for broader scopes.
  scopeResolvedColumnId: z.string().uuid().nullable(),
  // human-readable label for the scope target (org/dept/project/board name).
  label: z.string(),
  // why this scope is reachable, in plain language.
  reason: z.string(),
  // Publication reach already passed the role permission/catalog gate, while
  // explicit grants narrow to their validated action list. This typed axis
  // replaces the former '*' string sentinel.
  actionSource: z.enum(['catalog', 'explicit']),
  capabilities: accessCapabilitiesSchema,
});
export type EffectiveAccessScope = z.infer<typeof effectiveAccessScopeSchema>;

export const effectiveAccessSchema = z.object({
  subjectKind: accessSubjectKindSchema,
  subjectId: z.string().uuid(),
  // false when SCOPED_ACCESS_ENABLED is OFF — the viewer shows LEGACY reach
  // (everything in the org / founder super-admin) and says so.
  enforced: z.boolean(),
  // true when the subject is the account founder/owner with full account reach.
  founderOverride: z.boolean(),
  scopes: z.array(effectiveAccessScopeSchema),
  // for an agent: the inviter who caps it (§5), or null.
  cappedToInviterUserId: z.string().uuid().nullable(),
  // one-line plain-language summary for the Scope Card / viewer header.
  summary: z.string(),
});
export type EffectiveAccess = z.infer<typeof effectiveAccessSchema>;

// ── P6 §6.4 — Effective-access INSPECTOR test-query wire shape ────────
// The inspector's "Run a test query" box runs a kb_search-style query AS the
// chosen subject (a user OR an agent role), against an optional card/scope PATH,
// using the SAME server-derived resolver + retrieval the runtime uses — so the
// inspector cannot drift from reality (spec §6.4). The endpoint is admin-only +
// gated by KB_LEVEL_SCOPE_ENABLED on the gateway (degrades to a notice on 404).
//
// Each returned chunk carries its level provenance (the `scope` it matched +
// the resolved level id), so the admin can verify exactly what an agent at a
// given scope would retrieve while running the §5.2 default-deny checklist.
export const effectiveAccessTestQueryHitSchema = z.object({
  chunkId: z.string().nullable(),
  sourceId: z.string().nullable(),
  sourceTitle: z.string().nullable(),
  sourceKind: z.string().nullable(),
  // the kb_chunks.scope the row matched ('user_private'|'org'|'project'|
  // 'board'|'column'|'card'|'collection') — the level provenance badge.
  scope: z.string().nullable(),
  snippet: z.string().nullable(),
  score: z.number().nullable(),
});
export type EffectiveAccessTestQueryHit = z.infer<typeof effectiveAccessTestQueryHitSchema>;

export const effectiveAccessTestQueryResultSchema = z.object({
  // false when KB_LEVEL_SCOPE_ENABLED is OFF on the gateway — the inspector
  // shows a notice that the test reflects legacy (un-scoped) retrieval.
  scopedRetrieval: z.boolean(),
  // the server-derived VISIBLE scope set the query ran under (provenance for the
  // admin: "this subject can reach org X, project Y, board Z").
  visible: z.object({
    orgId: z.string().nullable(),
    projectIds: z.array(z.string()),
    boardIds: z.array(z.string()),
    columnIds: z.array(z.string()),
    cardIds: z.array(z.string()),
  }),
  // true when the subject is the account owner (sees everything) — the inspector
  // warns the admin that narrowing is masked by founderOverride.
  founderFullReach: z.boolean(),
  hits: z.array(effectiveAccessTestQueryHitSchema),
});
export type EffectiveAccessTestQueryResult = z.infer<typeof effectiveAccessTestQueryResultSchema>;

// ── Governance roster row (§1 ONE ROSTER, TWO BADGES) ────────────────
// One unified row per human OR agent reachable in an org, with its scope shown
// inline. Backs the Access & Governance screen.
export const governanceMemberSchema = z.object({
  subjectKind: accessSubjectKindSchema,
  subjectId: z.string().uuid(),
  displayName: z.string(),
  // humans: email; agents: role slug.
  secondary: z.string().nullable(),
  // org_role for humans (founder|admin|member|viewer) or null for agents.
  orgRole: z.string().nullable(),
  // a compact, plain-language scope label ("Whole org", "Marketing dept", …).
  scopeLabel: z.string(),
  // true if read-only (no canWrite grant) — drives the badge.
  readOnly: z.boolean(),
  // §5: for agents, the inviter that caps it.
  cappedToInviterUserId: z.string().uuid().nullable(),
});
export type GovernanceMember = z.infer<typeof governanceMemberSchema>;
